import {
    call,
    delay,
    put,
    race,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import storeNames from '@elab/components/connected/forms/CheckoutForms/storeNames';
import { getPathname } from '@elab/core/utils/address';
import * as address from '@elab/core/utils/address';
import analytics from '@elab/core/utils/analytics';
import authCookiesManager from '@elab/core/utils/authCookiesManager';
import {
    backSocialActions,
    checkoutFlow,
    cookieNames,
    loginErrors,
    loyaltyStatuses,
    providerIds,
    redirectLimitationBrandsUrlAfterCheckoutLogin,
    storageNames,
} from '@elab/core/utils/constants';
import { socialRegistrationUri } from '@elab/core/utils/social';
import { deleteAllCookies, deleteCookie, getCookie } from '@elab/core/utils/cookie';
import * as storage from '@elab/core/utils/storage';
import logger from '@elab/core/utils/logger';
import { parameters as URLParameters, getInternalURL, isExternalURL } from '@elab/core/utils/url';
import { hasLimitationBrands } from '@elab/core/utils/cart/sagaHelpers';
import { getSelectors as checkoutFormsSelectors } from '@elab/components/connected/forms/CheckoutForms/redux';
import {
    actions as updatePhoneActions,
    types as updatePhoneNumberTypes,
} from '@elab/components/connected/forms/UpdatePhoneNumberForm/redux';
import { actions as registrationActions } from '@elab/components/connected/forms/RegistrationForm/redux';
import { createActions as checkoutActions } from '@elab/components/connected/forms/CheckoutForms/redux';

import { actions as cartActions, selectors as cartSelectors, types as cartTypes } from '../cart';
import { selectors as configSelectors } from '../config';
import { actions as globalActions, selectors as globalSelectors } from '../global';
import {
    actions as socialActions,
    selectors as socialSelectors,
    types as socialTypes,
} from '../social';
import { globalTypes } from '../types';
import { actions as userActions, selectors as userSelectors, types as userTypes } from '../user';
import { actions as uiActions, types as uiTypes } from '../ui';

import * as api from './api';

import { actions, selectors, types } from '.';

const checkoutAuthOptionsSelectors = dynaForms.getSelectors(storeNames.CHECKOUT_AUTH_OPTIONS);

const CHECKOUT_GUEST_TO_LOGIN_FORM = 'checkoutGuestToLoginForm';
const LOGIN_CHECKOUT = 'checkout-login';
const LOGIN_AFTER_REGISTRATION = 'after-registration';

export function getURLParameters() {
    let afterLoginRedirectParam = address.getSearchParam(URLParameters.AFTER_LOGIN_REDIRECT_URL);
    const tokyWokyRedirect = address.getSearchParam(URLParameters.REDIRECT_TO_TOKYWOKY);
    const isPendingSubscription =
        address.getSearchParam(URLParameters.PENDING_OOS_SUBSCRIPTION) === 'true';

    if (afterLoginRedirectParam) {
        afterLoginRedirectParam = encodeURI(afterLoginRedirectParam);
    }

    return { afterLoginRedirectParam, isPendingSubscription, tokyWokyRedirect };
}

export function* authErrorHandlers() {
    const fbAuthId = yield select(selectors.getFbAuthId);
    if (fbAuthId) {
        sessionStorage.removeItem(`fbssls_${fbAuthId}`);
    }
    sessionStorage.removeItem('googleAuthToken');
    yield put(socialActions.setInitialSocialInfo());
    const fbButtonNode = document.querySelectorAll('.fb-lgn-btn');
    fbButtonNode.forEach(element => {
        element.classList.remove('social-login--disable');
    });
    const googleButtonNode = document.querySelectorAll('.social-login--google');
    googleButtonNode.forEach(element => {
        element.classList.remove('google-login--disable');
    });
}

export function* onInit(redirectUrl) {
    yield put(userActions.loadProfile());
    yield take(userTypes.SET_PROFILE);

    yield put(cartActions.loadCart());
    yield put(globalActions.redirect(redirectUrl));

    const socialData = yield select(socialSelectors.getSocialData);
    if (socialData && socialData.uid) {
        yield put(actions.setDataField('username', socialData.uid));
    }
}

export function* setSocialInfo({ payload: { action, data: { uid } = {} } }) {
    if (action === backSocialActions.login && uid) {
        const pwdNode = document.getElementById('password-input-field');
        if (pwdNode) {
            pwdNode.value = '';
        }
        yield put(actions.setDataField('username', uid));
    }
}

export function* initialize() {
    const isLoading = yield select(selectors.isLoading);

    if (isLoading) {
        return;
    }

    try {
        yield put(actions.setLoading(true));
        yield call(onInit);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* logout({ payload }) {
    const isLoading = yield select(selectors.isLoading);

    if (isLoading) {
        return;
    }

    try {
        yield put(actions.setLoading(true));

        if (!(payload && payload.alreadyLoggedOut)) {
            if (yield call(getCookie, cookieNames.tokyBepSso) && window.toky) {
                window.toky.logout();
            }
            yield call(api.logout);
        }

        sessionStorage.removeItem('userSession');
        sessionStorage.removeItem('googleAuthToken');
        localStorage.setItem('deleteUserSession', Date.now());
        const setAnalyticsCookies = yield select(configSelectors.setAnalyticsCookie);
        if (setAnalyticsCookies) {
            yield call(deleteCookie, 'analyticsData');
        }
        const cookiesToSpare = ['OptanonConsent', 'OptanonAlertBoxClosed', 'smartBannerDismissed'];
        yield call(deleteAllCookies, cookiesToSpare);
        yield call(
            storage.removeEverythingExcept,
            storage.key.country,
            storage.key.visitorId,
            storage.key.language,
        );
        yield put(actions.initializeDataField('', '', true));

        if ((payload && !payload.disableRedirect) || !payload) {
            yield put(actions.afterLogout());
            const redirectUrl =
                payload && !payload.alreadyLoggedOut
                    ? payload.successRedirectUrl || payload
                    : yield call(address.getURL);
            yield put(globalActions.goto(redirectUrl, true));
        }
    } catch (e) {
        yield call(logger.error, e);
        yield call(address.redirect, address.getURL());
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* login({ payload }) {
    const isLoading = yield select(selectors.isLoading);
    yield put(actions.setStopErrorResetPropagation(false));
    if (isLoading) {
        return;
    }

    let {
        successRedirectLink,
        username,
        failRedirectLink,
        password,
        rememberMe,
        mergeCarts = true,
        communityBaseUrl,
        preventRedirects,
        autologin,
        context,
        formType,
        globalAuthErrorMessage,
    } = payload;

    const { afterLoginRedirectParam, isPendingSubscription, tokyWokyRedirect } = yield call(
        getURLParameters,
    );

    if (tokyWokyRedirect && communityBaseUrl) {
        successRedirectLink =
            communityBaseUrl + getInternalURL(decodeURIComponent(tokyWokyRedirect));
    } else if (afterLoginRedirectParam) {
        successRedirectLink = getInternalURL(decodeURIComponent(afterLoginRedirectParam));
        const isUrlExternal = yield call(isExternalURL, successRedirectLink);
        if (isUrlExternal) {
            successRedirectLink = '/';
        }
    }

    username = (username || '').trim().toLowerCase();

    if (context !== LOGIN_AFTER_REGISTRATION && context !== LOGIN_CHECKOUT) {
        yield call(analytics.handleSubmitLoginEvent);
    }

    let loginInterrupted = false;
    try {
        yield put(actions.setLoading(true));
        const loginData = yield select(socialSelectors.getLoginData);
        yield call(api.login, username, password, rememberMe, loginData, autologin);
        yield call(analytics.handleLogin);

        if (context === LOGIN_CHECKOUT) {
            const customerLoyaltyStatus = yield select(
                checkoutFormsSelectors(CHECKOUT_GUEST_TO_LOGIN_FORM).getCustomerLoyaltyStatus,
            );

            yield spawn(
                analytics.handleCheckoutExisting,
                LOGIN_CHECKOUT,
                customerLoyaltyStatus ? customerLoyaltyStatus === loyaltyStatuses.registered : null,
            );
        }

        const updatePhoneEnable = yield call(updatePhoneFeatureEnabled, successRedirectLink || '');
        if (updatePhoneEnable) {
            yield put(
                updatePhoneActions.isCheckoutFlow({
                    checkoutFlow: true,
                    dataPayload: {
                        mergeCarts,
                        isPendingSubscription,
                        preventRedirects,
                        successRedirectLink,
                        getFailedUrl: failRedirectLink,
                    },
                }),
            );
        }
        yield put(userActions.loadProfile());
        if (updatePhoneEnable) {
            const { canContinue } = yield race({
                canContinue: take(userTypes.SET_PROFILE),
                cancel: take(updatePhoneNumberTypes.INTERRUPT_LOGIN),
            });
            if (!canContinue) {
                loginInterrupted = true;
                return;
            }
        } else {
            yield take(userTypes.SET_PROFILE);
        }

        yield put(actions.loginProfileReady());

        if (context !== LOGIN_AFTER_REGISTRATION && context !== LOGIN_CHECKOUT) {
            yield call(analytics.handleCompleteLoginEvent);
        }
        if (mergeCarts) {
            yield put(cartActions.mergeCart());
            yield take(cartTypes.CART_BEEN_MERGED);
        }

        yield put(cartActions.checkAddresses());
        yield take(cartTypes.CHECK_ADDRESSES_COMPLETE);

        yield put(userActions.setPendingSubscription(isPendingSubscription));

        const languageIsoCode = yield select(userSelectors.getLanguageIsoCode);
        const availableLanguages = yield select(globalSelectors.getAvailableLanguages);

        if (preventRedirects) {
            return;
        }

        const externalUrl = yield call(isExternalURL, successRedirectLink);

        if (languageIsoCode && availableLanguages.length > 1 && !externalUrl) {
            const targetUrl = yield call(setLimitativeUrl, successRedirectLink);

            yield put(
                globalActions.updateLanguage({
                    successRedirectUrl: targetUrl,
                    languageIsoCode,
                }),
            );
        } else {
            let targetUrl;
            if (successRedirectLink) {
                targetUrl = successRedirectLink;
            } else {
                const { pathname, search, hash } = window.location;
                targetUrl = pathname + search + hash;
            }

            targetUrl = yield call(setLimitativeUrl, targetUrl);

            yield put(globalActions.redirect(targetUrl));
            yield put(
                actions.setError({
                    errorType: '',
                }),
            );
        }
    } catch (e) {
        if (context === LOGIN_CHECKOUT) {
            yield spawn(analytics.handleFailedCheckoutAuth, LOGIN_CHECKOUT);
        }
        let errorType;
        const errorJson = e.json ? e.json : false;
        const errorCategory = errorJson ? errorJson.error : '';
        if (e.status === 403) {
            errorType = loginErrors.forbidden;
        } else if (errorCategory === loginErrors.invalidGrant) {
            errorType = loginErrors.invalidGrant;
        } else if (errorCategory === loginErrors.invalidSocialProfileGrant) {
            errorType = loginErrors.invalidSocialProfileGrant;
        } else if (errorCategory === loginErrors.invalidSocialProfileToken) {
            errorType = loginErrors.invalidSocialProfileToken;
            yield call(authErrorHandlers);
        } else if (errorCategory === loginErrors.invalidSocialProfileResource) {
            errorType = loginErrors.invalidSocialProfileResource;
            yield call(authErrorHandlers);
        } else if (errorCategory === loginErrors.authLocked) {
            yield put(actions.setStopErrorResetPropagation(true));
            errorType = loginErrors.authLocked;
        } else {
            errorType = loginErrors.general;
        }
        if (failRedirectLink) {
            yield put(globalActions.redirect(`${failRedirectLink}&errortype=${errorType}`));
        }

        if (formType === 'guestToLoginForm') {
            yield put(
                checkoutActions('checkout-guest').setGlobalError(
                    errorCategory === loginErrors.invalidGrant
                        ? globalAuthErrorMessage
                        : errorJson.error_description,
                    errorType,
                ),
            );
        } else {
            yield put(
                actions.setError({
                    errorType,
                    errorMessage: errorJson ? errorJson.error_description : null,
                }),
            );
        }
    } finally {
        yield put(actions.setLoading(false));
        if (!loginInterrupted) {
            yield put(actions.loginDone());
            yield put(uiActions.notifyIframe(JSON.stringify({ isLoggedIn: true })));
        } else {
            yield call(logout, { payload: { disableRedirect: true } });
            yield delay(200);
            yield put({
                type: 'checkout-auth-options.SET_DATA_FIELD_VALUE',
                payload: {
                    name: 'checkoutOptions',
                    value: 'checkout-guest',
                },
            });
        }
    }
}

function* updatePhoneFeatureEnabled(successRedirectLink) {
    const isCheckoutFlow = successRedirectLink.includes(checkoutFlow);
    const updatePhonePopUpEnabled = yield select(configSelectors.getVerifyPhonePopup);
    return isCheckoutFlow && updatePhonePopUpEnabled;
}

export function* setLimitativeUrl(targetUrl = '') {
    const entries = yield select(cartSelectors.getEntries);
    const hasLimitationBrand = yield call(hasLimitationBrands, entries);
    if (targetUrl.includes('checkout') && hasLimitationBrand) {
        const isLimitativeBrandsReady = yield select(cartSelectors.isLimitativeBrandsReady);

        if (!isLimitativeBrandsReady) {
            yield take(cartTypes.SET_LIMITATIVE_BRANDS);
        }

        const limitativeBrands = yield select(cartSelectors.getLimitativeCartEntries);
        if (limitativeBrands.length) {
            targetUrl = redirectLimitationBrandsUrlAfterCheckoutLogin;
        }
    }
    return targetUrl;
}

export function* checkUserToken() {
    const userId = yield select(userSelectors.getUserId);

    if (userId && authCookiesManager.isAnonymous()) {
        yield put(actions.doLogout({ alreadyLoggedOut: true }));
    }
}

export function* removeLastUsedStore() {
    const useRtsLastStore = yield select(configSelectors.useRtsLastStore);
    if (!useRtsLastStore) return;
    yield call(storage.remove, storageNames.lastUsedStore);
}

function* resetPassword({ payload }) {
    if (payload && payload.name && payload.name === (yield select(selectors.getLoginPopoverName))) {
        yield put(uiActions.setLoginPopoverOpened(payload.isOpened));
    }
}

export function* socialRegistration({
    payload: { autologinRedirectLink, failRedirectLink, socialName, accessToken, isRegPage },
}) {
    try {
        const providerId = providerIds[socialName];
        yield put(socialActions.setSocialInfo({ login: { accessToken, providerId } }));

        const socialUri = socialRegistrationUri(socialName);
        if (!socialUri) {
            return;
        }

        const pathName = (yield call(getPathname)).split('/');
        const selectedAuthOption = yield select(state =>
            checkoutAuthOptionsSelectors.getDataFieldValue(state, 'checkoutOptions'),
        );
        if (
            selectedAuthOption === 'checkout-registration' ||
            (pathName.length > 1 && pathName[1] === 'register')
        ) {
            isRegPage = true;
        } else if (selectedAuthOption === 'checkout-login' && !failRedirectLink) {
            failRedirectLink = '/login/checkout?error=true';
        }
        let availableActions;
        try {
            const { actions } = yield call(api.socialAuth, {
                socialUri,
                accessToken,
            });
            availableActions = actions;
        } catch (error) {
            const errorType = error.json.errors[0].type;
            if (isRegPage) {
                yield put(
                    registrationActions.setGlobalError(error.json.errors[0].message, errorType),
                );
                yield put(
                    checkoutActions('checkout-registration').setGlobalError(
                        error.json.errors[0].message,
                        errorType,
                    ),
                );
            } else {
                yield put(globalActions.redirect(`${failRedirectLink}&errortype=${errorType}`));
                yield put(
                    actions.setError({
                        errorType,
                        errorMessage: error.json.errors[0].message,
                    }),
                );
            }
            yield call(authErrorHandlers);
        }

        if (!availableActions) {
            return;
        }

        if (availableActions[0].action === backSocialActions.autologin) {
            yield put(
                actions.doLogin({ autologin: true, successRedirectLink: autologinRedirectLink }),
            );
            return;
        }

        if (socialName === 'facebook') {
            yield put(
                uiActions.openDialog({
                    name: 'facebook-dialog',
                    content: availableActions,
                }),
            );
        } else if (socialName === 'google') {
            yield put(
                uiActions.openDialog({
                    name: 'google-dialog',
                    content: availableActions,
                }),
            );
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export default [
    takeEvery(globalTypes.INITIALIZE, initialize),
    takeEvery(types.DO_LOGOUT, logout),
    takeEvery(types.DO_LOGIN, login),
    takeEvery(types.LOGIN_DONE, removeLastUsedStore),
    takeLatest(types.CHECK_USER_TOKEN, checkUserToken),
    takeLatest(uiTypes.SET_POPOVER_STATUS, resetPassword),
    takeEvery(socialTypes.SET_SOCIAL_INFORMATION, setSocialInfo),
    takeEvery(types.SOCIAL_REGISTRATION, socialRegistration),
];
